/**
 * Mockup Types
 * Defines platform mockup configurations for contextual ad previews
 */

import type { AdFormat } from '@/types/formats';

export interface MockupConfig {
  id: string;
  name: string;
  platform: string;
  // Scale factor for the ad within the mockup (1 = 100%)
  adScale?: number;
  // Aspect ratio of the mockup container
  aspectRatio?: number;
}

// Available mockup definitions
export const MOCKUP_CONFIGS: Record<string, MockupConfig> = {
  'mooncloak-search': {
    id: 'mooncloak-search',
    name: 'Mooncloak Search',
    platform: 'Mooncloak',
    adScale: 1,
  },
  'mooncloak-homepage': {
    id: 'mooncloak-homepage',
    name: 'Mooncloak Homepage',
    platform: 'Mooncloak',
    adScale: 1,
  },
  'mooncloak-gameday': {
    id: 'mooncloak-gameday',
    name: 'Mooncloak GameDay',
    platform: 'Mooncloak',
    adScale: 1,
  },
  'facebook-feed': {
    id: 'facebook-feed',
    name: 'Facebook Feed',
    platform: 'Facebook',
    adScale: 1,
  },
  'instagram-feed': {
    id: 'instagram-feed',
    name: 'Instagram Feed',
    platform: 'Instagram',
    adScale: 1,
  },
  'instagram-story': {
    id: 'instagram-story',
    name: 'Instagram Story',
    platform: 'Instagram',
    adScale: 1,
    aspectRatio: 9 / 16,
  },
  'google-search': {
    id: 'google-search',
    name: 'Google Search',
    platform: 'Google',
    adScale: 1,
  },
  'google-display': {
    id: 'google-display',
    name: 'Google Display',
    platform: 'Google',
    adScale: 1,
  },
  'twitter-feed': {
    id: 'twitter-feed',
    name: 'X/Twitter Feed',
    platform: 'Twitter',
    adScale: 1,
  },
};

// Map format IDs to their compatible mockups
export function getMockupsForFormat(format: AdFormat): MockupConfig[] {
  const mockups: MockupConfig[] = [];

  // Mooncloak formats
  if (format.provider === 'mooncloak') {
    if (format.id.includes('search')) {
      mockups.push(MOCKUP_CONFIGS['mooncloak-search']);
    }
    if (format.id.includes('homepage')) {
      mockups.push(MOCKUP_CONFIGS['mooncloak-homepage']);
    }
    if (format.id.includes('gameday')) {
      mockups.push(MOCKUP_CONFIGS['mooncloak-gameday']);
    }
    if (format.id.includes('display')) {
      mockups.push(MOCKUP_CONFIGS['google-display']);
    }
  }

  // Facebook formats
  if (format.provider === 'facebook' || format.platforms.toLowerCase().includes('facebook')) {
    if (format.id.includes('story')) {
      mockups.push(MOCKUP_CONFIGS['instagram-story']);
    } else {
      mockups.push(MOCKUP_CONFIGS['facebook-feed']);
    }
  }

  // Google formats
  if (format.provider === 'google') {
    mockups.push(MOCKUP_CONFIGS['google-display']);
  }

  // Instagram / Influencer formats
  if (format.provider === 'influencer') {
    if (format.id.includes('ig-story') || format.id.includes('tiktok')) {
      mockups.push(MOCKUP_CONFIGS['instagram-story']);
    }
    if (format.id.includes('ig-feed')) {
      mockups.push(MOCKUP_CONFIGS['instagram-feed']);
    }
    if (format.id.includes('twitter')) {
      mockups.push(MOCKUP_CONFIGS['twitter-feed']);
    }
  }

  // Square formats work well on Instagram and Facebook
  if (format.width === format.height && format.width >= 1080) {
    if (!mockups.some(m => m.id === 'instagram-feed')) {
      mockups.push(MOCKUP_CONFIGS['instagram-feed']);
    }
    if (!mockups.some(m => m.id === 'facebook-feed')) {
      mockups.push(MOCKUP_CONFIGS['facebook-feed']);
    }
  }

  // Landscape formats
  if (format.width > format.height * 1.5) {
    if (!mockups.some(m => m.id === 'facebook-feed')) {
      mockups.push(MOCKUP_CONFIGS['facebook-feed']);
    }
    if (!mockups.some(m => m.id === 'twitter-feed')) {
      mockups.push(MOCKUP_CONFIGS['twitter-feed']);
    }
  }

  return mockups;
}
